'use client';

import React, { useState, useMemo } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { HomeScreenSectionTypeEnum, ICmsSection } from '../services/types';
import { CMS_PAGE_COUNT, CMSSectionsAPi, MinimalStoreInput } from '../services/getCMS.service';
import { isValidSection, tryParseContent } from './cms.helpers';
import { CmsSectionRenderer } from './CmsSectionRenderer';
import InfiniteGrid from '@/src/components/InfiniteGrid';
import { useLiveStores } from '../services/clientService';

interface CmsListProps {
  sections: ICmsSection[];
  pageId: string;
  enableLiveTimes?: boolean;
}

const CmsList: React.FC<CmsListProps> = ({
  sections: initialSections,
  pageId,
  enableLiveTimes = false,
}) => {
  const queryClient = useQueryClient();
  const queryKey = useMemo(() => ['cmsSections', { pageSize: CMS_PAGE_COUNT, pageId }], [pageId]);

  const [currentSections, setCurrentSections] = useState<ICmsSection[]>(initialSections);

  React.useEffect(() => {
    const existingData = queryClient.getQueryData(queryKey);
    if (!existingData) {
      queryClient.setQueryData(queryKey, {
        pages: [initialSections],
        pageParams: [initialSections[initialSections.length - 1]?.index || 1],
      });
      setCurrentSections(initialSections);
    }
  }, [initialSections, queryKey, queryClient]);

  const allStoresPayload = useMemo(() => {
    if (!enableLiveTimes) return [];

    const activeSections = currentSections.length > 0 ? currentSections : initialSections;
    const seen = new Set<string>();

    return activeSections.filter(isValidSection).reduce<MinimalStoreInput[]>((acc, section) => {
      const { parsed: parsedContent, isValid } = tryParseContent(
        section.content,
        section.sectionsType,
      );

      if (!isValid || !parsedContent) return acc;

      const addStore = (id: number, branchId: number) => {
        if (!id && !branchId) return;
        const finalId = id || branchId;
        const finalBranchId = branchId || id;
        const key = `${finalId}-${finalBranchId}`;
        if (!seen.has(key)) {
          seen.add(key);
          acc.push({ id: finalId, branchId: finalBranchId });
        }
      };

      if (
        (section.sectionsType === HomeScreenSectionTypeEnum.NearbyList ||
          section.sectionsType === HomeScreenSectionTypeEnum.PromotedStores) &&
        Array.isArray(parsedContent)
      ) {
        parsedContent.forEach((s: any) => {
          addStore(Number(s.store?.id || s.id), Number(s.id));
        });
      }

      if (section.sectionsType === HomeScreenSectionTypeEnum.PromotedStore) {
        addStore(
          Number(parsedContent.StoreId),
          Number(parsedContent.BranchId) || Number(parsedContent.StoreId) || 0,
        );
      }

      if (
        section.sectionsType === HomeScreenSectionTypeEnum.PromotedProducts ||
        section.sectionsType === HomeScreenSectionTypeEnum.PromotedProduct ||
        section.sectionsType === HomeScreenSectionTypeEnum.TrendingProducts
      ) {
        const productsList = Array.isArray(parsedContent)
          ? parsedContent
          : (parsedContent?.products ??
            parsedContent?.Products ??
            (typeof parsedContent === 'object' && parsedContent !== null ? [parsedContent] : []));

        if (Array.isArray(productsList)) {
          productsList.forEach((p: any) => {
            const sId = Number(p.storeId ?? p.store?.id ?? p.branchId ?? 0);
            const bId = Number(p.branchId ?? p.storeId ?? p.store?.id ?? 0);
            addStore(sId, bId);
          });
        }
      }

      return acc;
    }, []);
  }, [currentSections, initialSections, enableLiveTimes]);

  const { data: globalLiveTimes } = useLiveStores(allStoresPayload);

  const fetchCmsSections = async (pageParam: number | string) => {
    const response = await CMSSectionsAPi.getCMSSectionsV2({
      lastSectionIndex: pageParam.toString(),
      pageSize: CMS_PAGE_COUNT,
      pageId,
    });
    return response as ICmsSection[];
  };

  return (
    <article>
      <InfiniteGrid<ICmsSection>
        queryKey={queryKey}
        initialData={initialSections}
        fetchFn={fetchCmsSections}
        pageSize={Number(CMS_PAGE_COUNT)}
        gridClassName="flex flex-col gap-8 text-center lg:gap-14 overflow-hidden"
        emptyComponent={''}
        onDataChange={setCurrentSections}
        getNextPageParamFn={(lastPage) => {
          return lastPage[lastPage.length - 1]?.index ?? '';
        }}
        renderItem={(section) => {
          if (!section) return null;
          return (
            <CmsSectionRenderer
              section={section}
              allProductsPageId={pageId}
              globalLiveTimes={enableLiveTimes ? globalLiveTimes : undefined}
            />
          );
        }}
      />
    </article>
  );
};

export default CmsList;
